import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DeliveryCostRuleOutputDto } from 'src/generated_client/model/deliveryCostRuleOutputDto';
import { DeliveryCostRuleInputDto } from 'src/generated_client/model/deliveryCostRuleInputDto';
import { DeliveryCostRuleUpdateDto } from 'src/generated_client/model/deliveryCostRuleUpdateDto';
import { DeliveryCostRuleService } from 'src/generated_client/api/delivery-cost-rule.service';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-cost-table',
  templateUrl: './cost-table.component.html',
  styleUrls: ['./cost-table.component.css']
})
export class CostTableComponent implements OnInit {
  costForm: FormGroup;
  isLoading = true;
  existingRule: DeliveryCostRuleOutputDto | null = null;

  constructor(
    private fb: FormBuilder,
    private deliveryCostRuleService: DeliveryCostRuleService,
    private dialog: MatDialog
  ) {
    this.costForm = this.fb.group({
      baseRate: ['', [Validators.required, Validators.min(0)]],
      baseDistanceKm: ['', [Validators.required, Validators.min(1)]],
      extraKmRate: ['', [Validators.required, Validators.min(0)]],
      roundTripReturnDiscountPercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
      waitFeePer10Min: ['', [Validators.required, Validators.min(0)]],
      oversizeItemFee: ['', [Validators.required, Validators.min(0)]],
      rainSurchargeBase: ['', [Validators.required, Validators.min(0)]],
      rainSurchargePerKm: ['', [Validators.required, Validators.min(0)]],
      productPaymentFeePercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]]
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadDeliveryCostRules();
  }

  private initializeForm(): void {
    this.costForm = this.fb.group({
      baseRate: ['', [Validators.required, Validators.min(0)]],
      baseDistanceKm: ['', [Validators.required, Validators.min(1)]],
      extraKmRate: ['', [Validators.required, Validators.min(0)]],
      roundTripReturnDiscountPercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]],
      waitFeePer10Min: ['', [Validators.required, Validators.min(0)]],
      oversizeItemFee: ['', [Validators.required, Validators.min(0)]],
      rainSurchargeBase: ['', [Validators.required, Validators.min(0)]],
      rainSurchargePerKm: ['', [Validators.required, Validators.min(0)]],
      productPaymentFeePercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]]
    });
  }

  private loadDeliveryCostRules(): void {
    this.isLoading = true;
    this.deliveryCostRuleService.getAll().subscribe({
      next: (rules) => {
        if (rules && rules.length > 0) {
          this.existingRule = rules[0];
          this.populateForm(this.existingRule);
        } else {
          // Initialize with default values if no rules exist
          this.populateForm({
            baseRate: 0,
            baseDistanceKm: 5, // Default base distance of 5km
            extraKmRate: 500, // Default extra km rate
            roundTripReturnDiscountPercent: 20, // Default 20% discount for round trips
            waitFeePer10Min: 1000, // Default wait fee
            oversizeItemFee: 2000, // Default oversize fee
            rainSurchargeBase: 1000, // Default rain surcharge base
            rainSurchargePerKm: 100, // Default rain surcharge per km
            productPaymentFeePercent: 5 // Default 5% product payment fee
          } as DeliveryCostRuleInputDto);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading delivery cost rules:', error);
        this.dialog.open(ConfirmDialogComponent, {
          width: '350px',
          data: {
            message: 'Error al cargar las reglas de costo',
            confirmButtonText: 'Aceptar',
            cancelButtonText: '',
            showCancelButton: false,
          },
        });
        this.isLoading = false;
      },
    });
  }

  private populateForm(rule: DeliveryCostRuleInputDto | DeliveryCostRuleOutputDto): void {
    this.costForm.patchValue(rule);
  }

  onSubmit(): void {
    if (this.costForm.invalid) {
      Object.values(this.costForm.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }

    this.isLoading = true;
    const formValue = this.costForm.value;
    
    if (this.existingRule) {
      // Update existing rule
      const updateDto: DeliveryCostRuleUpdateDto = {
        id: this.existingRule.id,
        ...formValue
      };
      
      this.deliveryCostRuleService.update(updateDto).subscribe({
        next: (updatedRule) => {
          this.existingRule = updatedRule;
          this.isLoading = false;
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
              message: 'Regla de costo actualizada correctamente',
              confirmButtonText: 'Aceptar',
              cancelButtonText: '',
              showCancelButton: false,
            },
          });
        },
        error: (error) => {
          console.error('Error updating cost rule', error);
          this.isLoading = false;
          this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
              message: 'Error al actualizar la regla de costo',
              confirmButtonText: 'Aceptar',
              cancelButtonText: '',
              showCancelButton: false,
            },
          });
        }
      });
    } else {
      // Create new rule
      this.deliveryCostRuleService.create(formValue as DeliveryCostRuleInputDto).subscribe({
        next: (id) => {
          this.isLoading = false;
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
              message: 'Regla de costo creada correctamente',
              confirmButtonText: 'Aceptar',
              cancelButtonText: '',
              showCancelButton: false,
            },
          });
          this.loadDeliveryCostRules(); // Reload to get the full rule with ID
        },
        error: (error) => {
          console.error('Error creating cost rule', error);
          this.isLoading = false;
          this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
              message: 'Error al crear la regla de costo',
              confirmButtonText: 'Aceptar',
              cancelButtonText: '',
              showCancelButton: false,
            },
          });
        }
      });
    }
  }
}
